import { SourceModelV2, SourceType } from 'Models/SourceModelV2';

/**
 * Check if the source is stale if the syncState is one of the following:
 * invalid-credentials / invalid / exception / sync-error / stopped
 * @param {string} source
 * @returns {boolean}
 */
export const isSourceStale = (source: string) => {
  return [
    'invalid-credentials',
    'invalid',
    'exception',
    'sync-error',
    'stopped',
  ].includes(source);
};

/**
 * Check if any of the sources are stale.
 * Calls {@link isSourceStale} to determine if a source is stale.
 * @param {SourceModelV2[]} sources - The array of source accounts to check.
 * @returns {boolean} True if any of the sources are stale, false otherwise.
 * @see isSourceStale
 */
export const hasStaleSources = (sources: SourceModelV2[]) => {
  return sources.some((source) => {
    return (
      source.sourceData.type !== 'icloud' &&
      isSourceStale(source.sourceData.syncState)
    );
  });
};

/**
 * Check if the source is a V3 source.
 * @param {string} sourceType - The type of source to check.
 * @returns {boolean} True if the source is a V3 source, false otherwise.
 */
const isV3SourceType = (sourceType?: SourceType): boolean => {
  return ['google', 'microsoft'].includes(sourceType);
};

/**
 * Check if the source is a V2 source.
 * @param {string} sourceType - The type of source to check.
 * @returns {boolean} True if the source is a V2 source, false otherwise.
 */
export const isV2SourceType = (sourceType?: SourceType): boolean => {
  const isV3 = isV3SourceType(sourceType);
  const isExcludedType = ['bhive', 'icloud'].includes(sourceType);
  return !isV3 && !isExcludedType;
};

/**
 * Check if the source should show a warning.
 * @param {boolean} isNylasV3Enabled - Whether Nylas V3 is enabled.
 * @param {string} sourceType - The type of source to check.
 * @param {string} syncState - The sync state of the source.
 * @returns {boolean} True if source is stale, V2 source, or iCloud source, and Nylas V3 is enabled. False otherwise.
 */
export const shouldShowWarning = (
  isNylasV3Enabled: boolean,
  sourceType?: SourceType,
  syncState?: string
): boolean => {
  if (!isNylasV3Enabled) {
    return false;
  }

  const isICloudSource = sourceType === 'icloud';
  const isV2Source = isV2SourceType(sourceType);
  const isStaleSource = isSourceStale(syncState);

  return isICloudSource || isV2Source || isStaleSource;
};
